import React, {useEffect} from 'react';
import {Layout} from '@/components/Layout/Layout';
import {WelcomeScreen} from '@/components/WelcomeScreen';
import {UserSection} from '@/components/Sections/UserSection';
import {PenaltiesSection} from '@/components/Sections/PenaltiesSection';
import {OrdersSection} from '@/components/Sections/OrdersSection';
import {CarsSection} from '@/components/Sections/CarsSection';
// import { CreditCardsSection } from '@/components/Sections/CreditCardsSection';
import {LoadingSpinner} from '@/components/UI/LoadingSpinner';
import {ErrorMessage} from '@/components/UI/ErrorMessage';
import {useAuth} from '@/hooks/useAuth';
import {useUserData, usePenalties, useParkings, useCars, useCreditCards} from '@/hooks/useApi';
import {useLocalStorage} from '@/hooks/useLocalStorage';
import {LOCAL_STORAGE_KEYS} from '@/utils/constants';
import type {Section} from '@/types';

const App: React.FC = () => {
    const {isAuthenticated, setAuthToken, logout, loading: authLoading} = useAuth();
    const [currentSection, setCurrentSection] = useLocalStorage<Section>(
        LOCAL_STORAGE_KEYS.LAST_SECTION,
        'user'
    );

    // API hooks
    const userData = useUserData();
    const penaltiesData = usePenalties();
    const parkingsData = useParkings();
    const carsData = useCars();
    const creditCardsData = useCreditCards();

    // Auto-load user data when authenticated
    useEffect(() => {
        if (isAuthenticated && currentSection === 'user') {
            userData.execute();
        }
    }, [isAuthenticated, currentSection]);

    const handleTokenSubmit = async (token: string) => {
        setAuthToken(token);
        // Try to load user data to validate token
        try {
            await userData.execute();
        } catch (error) {
            console.error('Failed to validate token:', error);
        }
    };

    const handleSectionChange = (section: Section) => {
        setCurrentSection(section);

        // Load data for the selected section
        switch (section) {
            case 'user':
                userData.execute();
                break;
            case 'penalties':
                penaltiesData.execute();
                break;
            case 'parkings':
                parkingsData.execute();
                break;
            case 'cars':
                carsData.execute();
                break;
            case 'credit-cards':
                creditCardsData.execute();
                break;
        }
    };

    const handleRefresh = () => {
        switch (currentSection) {
            case 'user':
                userData.execute();
                break;
            case 'penalties':
                penaltiesData.execute();
                break;
            case 'parkings':
                parkingsData.execute();
                break;
            case 'cars':
                carsData.execute();
                break;
            case 'credit-cards':
                creditCardsData.execute();
                break;
        }
    };

    const getCurrentData = () => {
        switch (currentSection) {
            case 'user':
                return userData;
            case 'penalties':
                return penaltiesData;
            case 'parkings':
                return parkingsData;
            case 'cars':
                return carsData;
            case 'credit-cards':
                return creditCardsData;
            default:
                return userData;
        }
    };

    const renderSectionContent = () => {
        const currentData = getCurrentData();

        if (currentData.loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <LoadingSpinner size="lg" text="Loading data..."/>
                </div>
            );
        }

        if (currentData.error) {
            return (
                <ErrorMessage
                    message={currentData.error}
                    onRetry={handleRefresh}
                />
            );
        }

        switch (currentSection) {
            case 'user':
                return userData.data ? (
                    <UserSection user={userData.data}/>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No user data available</p>
                    </div>
                );

            case 'penalties':
                return <PenaltiesSection penalties={penaltiesData.data || []}/>;

            case 'parkings':
                return <OrdersSection orders={parkingsData.data || []}/>;

            case 'cars':
                return <CarsSection cars={carsData.data || []}/>;

            case 'credit-cards':
                return (
                    <div className="text-center py-12">
                        <p className="text-gray-500">Under Construction</p>
                    </div>
                )
            // return <CreditCardsSection creditCards={creditCardsData.data || []} />;

            default:
                return (
                    <div className="text-center py-12">
                        <p className="text-gray-500">Section not found</p>
                    </div>
                );
        }
    };

    // Show loading screen while checking authentication
    if (authLoading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <LoadingSpinner size="lg" text="Initializing..."/>
            </div>
        );
    }

    // Show welcome screen if not authenticated
    if (!isAuthenticated) {
        return <WelcomeScreen onTokenSubmit={handleTokenSubmit}/>;
    }

    const currentData = getCurrentData();

    return (
        <Layout
            currentSection={currentSection}
            onSectionChange={handleSectionChange}
            onLogout={logout}
            onRefresh={handleRefresh}
            loading={currentData.loading}
        >
            {renderSectionContent()}
        </Layout>
    );
};

export default App;
